import { Event, Component, h, Prop, Host, State, Watch, EventEmitter } from '@stencil/core'
import {
  MainNavigationOption,
  MainNavigationOptionClickedEvent,
  MainNavigationType,
  MainNavigationUpdatedEvent, sendFullStoryEvent,
  ToolSwitcherItem, ToolSwitcherUpdatedEvent,
} from '../../utils'

@Component({
  tag: 'snap-main-navigation',
  styleUrl: 'snap-main-navigation.scss',
})
export class SnapMainNavigation {
  /**
   * Sets the theme for the component
   */
  @Prop({ reflect: true }) theme: MainNavigationType = 'mobile'
  @Prop({ reflect: true }) corner: boolean = true
  @Prop({ reflect: true }) toolSwitcher: boolean = false
  /**
   * List of navigation options
   */
  @Prop() navigationOptionsInput: MainNavigationOption[]
  @State() navigationOptions: MainNavigationOption[]
  @Event() navigationupdate: EventEmitter<MainNavigationUpdatedEvent>
  @Event({ eventName: 'snap-tool-switcher-update', bubbles: true, composed: true }) snapToolSwitcherUpdate: EventEmitter<ToolSwitcherUpdatedEvent>

  @Prop() toolSwitcherOptions: Array<ToolSwitcherItem> = []
  @State() toolSwitcherOptionsCopy: Array<ToolSwitcherItem> = []
  @State() toolSwitcherOpen: boolean = false
  @State() menuItemsAmountDiff = 1

  @Watch('navigationOptionsInput')
  onNavigationOptionsInput(input: MainNavigationOption[]) {
    if (this.navigationOptionsInput) {
      this.navigationOptions = [...input]
      this.navigationOptions.length === 5 ? this.menuItemsAmountDiff = 1 : this.menuItemsAmountDiff = 2
    }
  }

  @Watch('toolSwitcherOptions')
  onOptionsUpdate() {
    if (this.toolSwitcherOptions) {
      this.toolSwitcherOptionsCopy = [...this.toolSwitcherOptions]
    }
  }

  @State() isModalOpen = false
  private MAX_ELEMENTS = 6

  componentWillLoad() {
    this.onNavigationOptionsInput(this.navigationOptionsInput)
    this.onOptionsUpdate()
    if (!this.navigationOptionsInput) {
      console.error('No navigation options provided')
    } else if (this.navigationOptionsInput.filter(option => !!option.active).length > 1) {
      console.error('More than one navigation item was selected')
    } else {
      if (this.navigationOptionsInput.filter(option => !!option.active).length === 0) {
        console.warn('No navigation option selected')
      }
      this.navigationOptions = [...this.navigationOptionsInput]
    }
  }

  getOptionElement = (option: MainNavigationOption): HTMLSnapMainNavigationElement => {
    return (
      <snap-main-navigation-option
        id={option.id}
        icon={option.icon}
        path={option.path}
        active={option.active}
        indicator={option.indicator}
        targetBlank={option.targetBlank}
        theme={this.theme}
        forceNavigation={option.forceNavigation}>
        {option.text}
        <span class="rounded-line"></span>
      </snap-main-navigation-option>
    )
  }

  optionClicked = (ev: CustomEvent<MainNavigationOptionClickedEvent>) => {
    if (ev.detail.id !== 'snap-main-navigation-modal-option') {
      sendFullStoryEvent('Navigation Clicked', {
        navType: ' snap-main-navigation',
        navLevel: 'main',
        buttonText: ev.detail.text,
        url: window.location.href,
        path: window.location.pathname
      })

      const active = this.navigationOptions.find(option => !!option.active)
      this.navigationupdate.emit({
        id: ev.detail.id,
        path: ev.detail.path,
      })
      if (!active || active.id !== ev.detail.id) {
        this.navigationOptions = [
          ...this.navigationOptions.map((option): MainNavigationOption => {
            return {
              ...option,
              active: option.id === ev.detail.id,
            }
          }),
        ]
      }
    }
  }

  render() {
    return (
      <Host
        on-snap-tool-switcher-open={ (e) => { this.toolSwitcherOpen = e.detail }}
        on-snap-tool-switcher-udate={ (e) => { this.snapToolSwitcherUpdate.emit(e) }}
        class={{['tool-switcher-open']: this.toolSwitcherOpen}}
        onMainNavigationOptionClicked={this.optionClicked}
      >
        {!this.isModalOpen && (
          <div>
            <div
              class={`snap-main-navigation snap-main-navigation--${this.theme} ${
                this.toolSwitcher ? 'snap-main-navigation-toolswitcher' : ''
              }`}>
              {this.corner && (
                <div class="snap-main-navigation-сorner-wrapper">
                  <div class="snap-main-navigation-сorner">
                    <div class="circle"></div>
                  </div>
                </div>
              )}
              <div class="snap-main-navigation-container-outer">
                <div class="snap-main-navigation-container-inner">
                  {this.toolSwitcher && (
                    <div>
                      <div class="snap-main-navigation-tool-switcher-container">
                        <snap-tool-switcher theme={this.theme} options={this.toolSwitcherOptionsCopy}></snap-tool-switcher>
                      </div>
                      <snap-tool-switcher
                        theme={this.theme}
                        class="snap-tool-switcher-desktop"
                        options={this.toolSwitcherOptionsCopy}></snap-tool-switcher>
                    </div>
                  )}

                  <div class="snap-nav-container">
                    <nav class="snap-nav" aria-label="MainNavigation">
                      {!!this.navigationOptions && this.navigationOptions.length !== 0 && this.navigationOptions.map(this.getOptionElement)}
                    </nav>
                  </div>
                </div>
              </div>
            </div>
            <div class={`snap-main-navigation-mobile snap-main-navigation--${this.theme}`}>
              {this.toolSwitcher && (
                <div class="snap-main-navigation-tool-switcher-container">
                  <snap-tool-switcher theme={this.theme} options={this.toolSwitcherOptionsCopy}></snap-tool-switcher>
                </div>
              )}
              <div class="snap-main-navigation-mobile-container">
                <div class="snap-nav-mobile-container">
                  <nav class="snap-nav-mobile" aria-label="MainNavigation">
                    {!!this.navigationOptions &&
                      this.navigationOptions.length !== 0 &&
                      this.navigationOptions.slice(0, this.MAX_ELEMENTS - this.menuItemsAmountDiff).map(this.getOptionElement)}
                    {!!this.navigationOptions && this.navigationOptions.length >= this.MAX_ELEMENTS && (
                      <snap-main-navigation-option
                        id="snap-main-navigation-modal-option"
                        icon="dots-horizontal-line"
                        onClick={() => (this.isModalOpen = true)}>
                        More
                      </snap-main-navigation-option>
                    )}
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
        {this.isModalOpen && (
          <div class={`snap-main-navigation-modal snap-main-navigation--${this.theme}`}>
            <div class="snap-main-navigation-modal-fullscreen">
              <div class="modal-content">
                <div class="modal-header">
                  More Options
                  <snap-icon size="md" icon="x-solid" onClick={() => (this.isModalOpen = false)}></snap-icon>
                </div>
                <div class="modal-body">
                  {!!this.navigationOptions && this.navigationOptions.length !== 0 && this.navigationOptions.map(this.getOptionElement)}
                </div>
              </div>
            </div>
          </div>
        )}
      </Host>
    )
  }
}
